import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../lib/api';

interface CartItem {
  id: string;
  quantity: number;
  product: {
    id: string;
    name: string;
    slug: string;
    price: number;
    thumbnailUrl: string | null;
    stockType: 'unlimited' | 'limited';
    stockQuantity: number;
  };
  seller: {
    id: string;
    storeName: string;
    storeSlug: string;
  };
}

// Guest cart item stored in localStorage (minimal data)
interface GuestCartItem {
  productId: string;
  quantity: number;
}

interface CartState {
  items: CartItem[];
  total: number;
  itemCount: number;
  isLoading: boolean;
  // Guest cart stored locally
  guestCart: GuestCartItem[];

  fetchCart: () => Promise<void>;
  addItem: (productId: string, quantity?: number, isAuthenticated?: boolean) => Promise<void>;
  updateItem: (itemId: string, quantity: number, isAuthenticated?: boolean) => Promise<void>;
  removeItem: (itemId: string, isAuthenticated?: boolean) => Promise<void>;
  clearCart: (isAuthenticated?: boolean) => Promise<void>;
  // Guest cart specific
  loadGuestCart: () => Promise<void>;
  mergeGuestCart: () => Promise<void>;
  clearGuestCart: () => void;
  getGuestCart: () => GuestCartItem[];
}

interface CartResponse {
  cart: {
    items: CartItem[];
    total: number;
    itemCount: number;
  };
}

interface ProductResponse {
  product: {
    id: string;
    name: string;
    slug: string;
    price: number;
    thumbnailUrl: string | null;
    stockType: 'unlimited' | 'limited';
    stockQuantity: number;
    seller: {
      id: string;
      storeName: string;
      storeSlug: string;
    };
  };
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      total: 0,
      itemCount: 0,
      isLoading: false,
      guestCart: [],

      fetchCart: async () => {
        set({ isLoading: true });
        try {
          const response = await api.get<CartResponse>('/cart');
          const { items, total, itemCount } = response.data!.cart;
          set({ items, total, itemCount, isLoading: false });
        } catch {
          set({ items: [], total: 0, itemCount: 0, isLoading: false });
        }
      },

      // Load guest cart by fetching product details for each item
      loadGuestCart: async () => {
        const { guestCart } = get();
        if (guestCart.length === 0) {
          set({ items: [], total: 0, itemCount: 0, isLoading: false });
          return;
        }

        set({ isLoading: true });
        const loadedItems: CartItem[] = [];

        for (const guestItem of guestCart) {
          try {
            const response = await api.get<ProductResponse>(`/products/${guestItem.productId}`);
            const product = response.data!.product;

            // Only add if product is still available
            if (product.stockType === 'unlimited' || product.stockQuantity > 0) {
              loadedItems.push({
                id: guestItem.productId, // Use productId as item id for guests
                quantity: guestItem.quantity,
                product: {
                  id: product.id,
                  name: product.name,
                  slug: product.slug,
                  price: product.price,
                  thumbnailUrl: product.thumbnailUrl,
                  stockType: product.stockType,
                  stockQuantity: product.stockQuantity,
                },
                seller: product.seller,
              });
            }
          } catch {
            // Product not found or unavailable, skip it
          }
        }

        const total = loadedItems.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
        const itemCount = loadedItems.reduce((sum, item) => sum + item.quantity, 0);

        set({ items: loadedItems, total, itemCount, isLoading: false });
      },

      addItem: async (productId: string, quantity = 1, isAuthenticated = false) => {
        if (isAuthenticated) {
          await api.post('/cart/items', { productId, quantity });
          await get().fetchCart();
        } else {
          // Guest mode: update local cart
          const { guestCart } = get();
          const existingIndex = guestCart.findIndex(item => item.productId === productId);

          let newGuestCart: GuestCartItem[];
          if (existingIndex >= 0) {
            newGuestCart = guestCart.map((item: GuestCartItem, index: number) =>
              index === existingIndex
                ? { ...item, quantity: item.quantity + quantity }
                : item
            );
          } else {
            newGuestCart = [...guestCart, { productId, quantity }];
          }

          set({ guestCart: newGuestCart });
          await get().loadGuestCart();
        }
      },

      updateItem: async (itemId: string, quantity: number, isAuthenticated = false) => {
        if (isAuthenticated) {
          await api.put(`/cart/items/${itemId}`, { quantity });
          await get().fetchCart();
        } else {
          // Guest mode: itemId is productId
          const { guestCart } = get();
          const newGuestCart = guestCart.map((item: GuestCartItem) =>
            item.productId === itemId
              ? { ...item, quantity }
              : item
          );
          set({ guestCart: newGuestCart });
          await get().loadGuestCart();
        }
      },

      removeItem: async (itemId: string, isAuthenticated = false) => {
        if (isAuthenticated) {
          await api.delete(`/cart/items/${itemId}`);
          await get().fetchCart();
        } else {
          // Guest mode: itemId is productId
          const { guestCart } = get();
          const newGuestCart = guestCart.filter((item: GuestCartItem) => item.productId !== itemId);
          set({ guestCart: newGuestCart });
          await get().loadGuestCart();
        }
      },

      clearCart: async (isAuthenticated = false) => {
        if (isAuthenticated) {
          await api.delete('/cart');
        }
        set({ items: [], total: 0, itemCount: 0, guestCart: [] });
      },

      // Merge guest cart with server cart after login
      mergeGuestCart: async () => {
        const { guestCart } = get();
        if (guestCart.length === 0) return;

        try {
          // Send guest cart items to server for merging
          await api.post('/cart/merge', { items: guestCart });
          // Clear guest cart after successful merge
          set({ guestCart: [] });
          // Fetch the merged cart from server
          await get().fetchCart();
        } catch {
          // If merge fails, just fetch server cart
          await get().fetchCart();
        }
      },

      clearGuestCart: () => {
        set({ guestCart: [], items: [], total: 0, itemCount: 0 });
      },

      getGuestCart: () => {
        return get().guestCart;
      },
    }),
    {
      name: 'cart-storage',
      partialize: (state: CartState) => ({ guestCart: state.guestCart }),
    }
  )
);
